import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Terminal from "../components/Terminal";
import TopologyDiagram from "../components/TopologyDiagram";
import GradingModal from "../components/GradingModal";
import PcConfigModal from "../components/PcConfigModal";
import PcBrowserModal from "../components/PcBrowserModal";
import PcMenu from "../components/PcMenu";
import { ArrowLeft, ArrowRight, CheckSquare, ChevronDown, ChevronUp, Lightbulb, Award, RotateCcw, X, Loader2, GripHorizontal, GripVertical, Menu, Minimize2, Maximize2, Cpu, Volume2, VolumeX } from "lucide-react";
import UpgradeModal from "../components/UpgradeModal";
import BootRealIOS from "../components/BootRealIOS";
import { FaultLabBanner } from "../components/FaultLabBanner";
import { toast } from "sonner";
import useLabSounds from "../hooks/useLabSounds";

export default function LabWorkspace() {
  const { labId } = useParams();
  const nav = useNavigate();

  const [lab, setLab] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  // Billing state — used to pre-empt the Start Lab button when a free-tier
  // user has already burned their 3 labs this month (bug #3 in round 14).
  const [billing, setBilling] = useState(null);
  const [grading, setGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState(null);
  const [gradeOpen, setGradeOpen] = useState(false);

  const [hintOpen, setHintOpen] = useState(false);
  const [hintQ, setHintQ] = useState("");
  const [hintResp, setHintResp] = useState("");
  const [hintLoading, setHintLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileTab, setMobileTab] = useState("terminal"); // mobile-only tab: "terminal" | "topology" | "objectives"
  // Multi-device console state. activeDevices is [primary] or [primary, secondary].
  // splitMode toggles the second pane. For single-device labs both are unused.
  const [activeDevices, setActiveDevices] = useState([]);
  const [splitMode, setSplitMode] = useState(false);
  // PC IP-configuration modal
  const [pcConfigOpen, setPcConfigOpen] = useState(false);
  const [pcConfigDevice, setPcConfigDevice] = useState(null); // {id, label}
  const [pcConfigInitial, setPcConfigInitial] = useState(null);
  // PC web browser modal + click-to-show popup menu
  const [pcBrowserOpen, setPcBrowserOpen] = useState(false);
  const [pcBrowserDevice, setPcBrowserDevice] = useState(null);
  const [pcMenu, setPcMenu] = useState({ open: false, deviceId: null });
  // Upgrade modal — triggered when a free-tier 402 comes back from /start or /hint.
  const [upgrade, setUpgrade] = useState({ open: false, reason: null });
  // Real IOS (CML) in-place panel swap — Layer 2 of Reconnecting-WS architecture.
  // When true, the Terminal panel is replaced by <BootRealIOS />; everything else
  // (topology, objectives, grade, hint) keeps working against the sim session.
  const [realIosMode, setRealIosMode] = useState(false);
  // v2 session id from BootRealIOS (set when real-IOS console is attached).
  // When non-null, the Grade button routes to /cml/v2/sessions/{id}/grade
  // so we grade against the REAL CML running-config (Round 34).
  const [v2SessionId, setV2SessionId] = useState(null);
  // Audio feedback — right/wrong per command + right/wrong on grade result.
  // Mute state is shared across the app via localStorage.
  const sounds = useLabSounds();
  // CLI Quick Reference collapsible. Persists per session via sessionStorage.
  const [cliRefOpen, setCliRefOpen] = useState(() => {
    try {
      const saved = sessionStorage.getItem("ciscolab.cliRefOpen");
      if (saved !== null) return saved === "1";
    } catch {
      // sessionStorage may be unavailable in some contexts (private mode etc.)
    }
    // Default: collapsed on mobile, collapsed on desktop too (per spec).
    return false;
  });
  const toggleCliRef = () => {
    setCliRefOpen((cur) => {
      const next = !cur;
      try { sessionStorage.setItem("ciscolab.cliRefOpen", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };
  const sidebarPanelRef = useRef(null);
  // Ref + state for the topology pane so the user can collapse it to give
  // the console the full vertical space, then re-expand it.
  const topoPanelRef = useRef(null);
  const [topoCollapsed, setTopoCollapsed] = useState(false);
  const toggleTopo = () => {
    const p = topoPanelRef.current;
    if (!p) return;
    if (topoCollapsed) {
      p.expand();
      setTopoCollapsed(false);
    } else {
      p.collapse();
      setTopoCollapsed(true);
    }
  };

  const toggleSidebar = () => {
    const panel = sidebarPanelRef.current;
    if (!panel) return;
    if (panel.isCollapsed()) {
      panel.expand();
      setSidebarOpen(true);
    } else {
      panel.collapse();
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    // Reset all per-lab state whenever the lab id changes — otherwise the
    // previous lab's session_id, gradeResult and active-device selections
    // leak into the new lab causing wrong-lab grading/hints and stale
    // terminal commands (bugs #4 + #5).
    setSession(null);
    setGradeResult(null);
    setGradeOpen(false);
    setActiveDevices([]);
    setSplitMode(false);
    setHintResp("");
    setHintQ("");
    setLoading(true);
    // The workspace used to persist panel sizes via `autoSaveId`s; we now
    // want every lab to start with the default proportions, so wipe stale
    // localStorage entries and expand the topology pane if it was collapsed
    // on the previous lab. Both are cheap + idempotent.
    try {
      for (const k of [
        "PanelGroup:sizes:lab-workspace-h-v3",
        "PanelGroup:sizes:lab-workspace-v-v2",
        "PanelGroup:sizes:terminal-split-v1",
      ]) localStorage.removeItem(k);
    } catch { /* ignore — private mode etc. */ }
    if (topoPanelRef.current) {
      try { topoPanelRef.current.expand(); } catch { /* noop */ }
    }
    setTopoCollapsed(false);
  }, [labId]);

  useEffect(() => {
    (async () => {
      try {
        const [labRes, billRes] = await Promise.all([
          api.get(`/labs/${labId}`),
          api.get("/billing/status").catch(() => ({ data: null })),
        ]);
        setLab(labRes.data);
        setBilling(billRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [labId]);

  const start = async () => {
    // Pre-empt: if this is a free-tier user at quota AND the lab isn't
    // already started/completed, show the upgrade modal instead of hitting
    // the backend (round-14 bug #3).
    const quotaLocked =
      billing &&
      billing.tier === "free" &&
      (billing.labs_used ?? 0) >= (billing.labs_limit ?? 3) &&
      lab?.status === "not_started" &&
      !lab?.is_pro_only;
    if (quotaLocked) {
      setUpgrade({
        open: true,
        reason: "You've used your 3 free labs this month. Upgrade to Pro for unlimited access to all 72 labs.",
      });
      return;
    }
    // Pro-only multi-device lab on a free account.
    if (lab?.is_pro_only && billing?.tier !== "pro") {
      setUpgrade({ open: true, reason: "This is a Pro-only multi-device lab" });
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post(`/labs/${labId}/start`);
      setSession(data);
      if (data.is_multi_device && data.devices && data.devices.length) {
        setActiveDevices([data.devices[0].id]);
      } else {
        setActiveDevices([]);
      }
      setSplitMode(false);
    } catch (err) {
      if (err?.response?.status === 402) {
        setUpgrade({ open: true, reason: err.response.data?.detail || "Free tier limit reached" });
      } else {
        throw err;
      }
    } finally {
      setStarting(false);
    }
  };

  const handleDeviceClick = (deviceId, opts = {}) => {
    if (!session?.is_multi_device) return;
    const dev = (session.devices || []).find((d) => d.id === deviceId);
    // PC click — show the popup menu (console / IP config / web browser).
    // Shift-click on a PC still goes to secondary-console behaviour.
    if (dev?.device_type === "pc" && !opts.secondary) {
      setPcMenu({ open: true, deviceId });
      return;
    }
    setActiveDevices((cur) => {
      if (opts.secondary && !realIosMode) {
        if (cur[0] === deviceId) return cur;
        setSplitMode(true);
        return [cur[0], deviceId];
      }
      if (opts.secondary && realIosMode && cur[0] !== deviceId) {
        toast.info("Split console isn't available in Real IOS mode yet — switching console instead.");
      }
      if (cur[0] === deviceId) return cur;
      if (cur[1] === deviceId) return [deviceId, cur[0]];
      return [deviceId, cur[1]].filter(Boolean);
    });
  };

  const closeSecondaryConsole = () => {
    setActiveDevices((cur) => cur.slice(0, 1));
    setSplitMode(false);
  };

  const toggleSplit = () => {
    if (!session?.is_multi_device) return;
    if (splitMode) {
      closeSecondaryConsole();
      return;
    }
    // Enable split: pick a different device as secondary
    const others = (session.devices || []).filter((d) => d.id !== activeDevices[0]);
    if (!others.length) return;
    setActiveDevices((cur) => [cur[0], others[0].id]);
    setSplitMode(true);
  };

  // Open the PC IP-configuration modal for the given device id. Pulls the
  // current IP/mask/gateway from a fresh /running-config call so the form
  // reflects what the backend has stored.
  const openPcConfig = async (deviceId) => {
    const dev = (session?.devices || []).find((d) => d.id === deviceId);
    if (!dev) return;
    let initial = { ip: "", mask: "", gateway: "" };
    try {
      const { data } = await api.get(`/labs/session/${session.session_id}/running-config?device_id=${deviceId}`);
      const rc = data?.running_config || "";
      const m1 = rc.match(/IPv4 Address[. ]*:\s*(\S+)/i);
      const m2 = rc.match(/Subnet Mask[. ]*:\s*(\S+)/i);
      const m3 = rc.match(/Default Gateway[. ]*:\s*(\S+)/i);
      const sane = (v) => (v && v !== "(none)" ? v : "");
      initial = { ip: sane(m1?.[1]), mask: sane(m2?.[1]), gateway: sane(m3?.[1]) };
    } catch {
      // ignore — modal will just show blanks
    }
    setPcConfigDevice({ id: dev.id, label: dev.label });
    setPcConfigInitial(initial);
    setPcConfigOpen(true);
  };

  // Open the PC web browser modal — entered URL is pinged to test reachability.
  const openPcBrowser = (deviceId) => {
    const dev = (session?.devices || []).find((d) => d.id === deviceId);
    if (!dev) return;
    setPcBrowserDevice({ id: dev.id, label: dev.label });
    setPcBrowserOpen(true);
  };

  // Open the PC console (primary) — same path the menu's "Command Prompt"
  // entry takes: just swap the active device to this PC.
  const openPcConsole = (deviceId) => {
    setActiveDevices((cur) => {
      if (cur[0] === deviceId) return cur;
      if (cur[1] === deviceId) return [deviceId, cur[0]];
      return [deviceId, cur[1]].filter(Boolean);
    });
  };

  // Resolve which device (if any) is currently primary so we can flag PC mode.
  const primaryDeviceMeta = (() => {
    if (!session?.is_multi_device) return null;
    const id = activeDevices[0];
    return (session.devices || []).find((d) => d.id === id) || null;
  })();
  const primaryIsPc = primaryDeviceMeta?.device_type === "pc";

  const grade = async () => {
    if (!session?.session_id) return;
    setGrading(true);
    try {
      // Round 34: when running on real CML, grade against the real
      // running-config via the v2 endpoint. Otherwise fall back to the
      // simulator grader (same UX, different ground truth).
      if (realIosMode && v2SessionId) {
        const { data } = await api.post(`/cml/v2/sessions/${v2SessionId}/grade`);
        // Normalize v2 response shape → GradingModal/{score, results}.
        const normalized = {
          score: data.score,
          results: (data.checks || []).map((c) => ({
            id: c.id,
            description: c.description,
            passed: c.passed,
            required: c.required,
          })),
          passed: data.passed,
          required_total: data.required_total,
          required_passed: data.required_passed,
          source: "real_ios",
        };
        setGradeResult(normalized);
        setGradeOpen(true);
        // Round 39 — audio feedback on grade outcome (3s "win" or "lose" clip).
        if (normalized.passed) sounds.playLabRight();
        else sounds.playLabWrong();
      } else {
        const { data } = await api.post(`/labs/session/${session.session_id}/grade`);
        setGradeResult(data);
        setGradeOpen(true);
        // Simulator payload uses either `passed` boolean or a score field —
        // treat >=100 as pass to stay consistent with the "Next lab" gate.
        const simPassed = data?.passed === true || Number(data?.score) >= 100;
        if (simPassed) sounds.playLabRight();
        else sounds.playLabWrong();
      }
    } finally {
      setGrading(false);
    }
  };

  const retry = async () => {
    setGradeOpen(false);
    setGradeResult(null);
    await start();
  };

  const askHint = async () => {
    if (!session?.session_id) return;
    setHintLoading(true);
    setHintResp("");
    try {
      const { data } = await api.post(`/labs/session/${session.session_id}/hint`, {
        question: hintQ || null,
      });
      setHintResp(data.hint);
    } catch (err) {
      if (err?.response?.status === 402) {
        setHintOpen(false);
        setUpgrade({ open: true, reason: err.response.data?.detail || "Free hint limit reached" });
      } else {
        throw err;
      }
    } finally {
      setHintLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="max-w-[1800px] mx-auto px-6 py-12 text-zinc-500 font-mono text-sm" data-testid="lab-loading">
          loading lab…
        </div>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="max-w-[1800px] mx-auto px-6 py-12">
          Lab not found. <Link className="text-[#00E5FF]" to="/labs">Back to catalog</Link>
        </div>
      </div>
    );
  }

  // Before starting → show brief/intro
  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <main className="max-w-4xl mx-auto px-6 py-10">
          <Link
            to="/labs"
            className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1 font-mono mb-6"
            data-testid="lab-back"
          >
            <ArrowLeft size={12} /> back to catalog
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">
            {lab.domain} · {lab.topic}
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4" data-testid="lab-title">{lab.title}</h1>
          <div className="flex items-center gap-3 text-xs font-mono text-zinc-400 mb-8">
            <span className="uppercase tracking-widest">{lab.difficulty}</span>
            <span>·</span>
            <span>device: {lab.device_type}</span>
            {lab.progress?.best_score > 0 && (
              <>
                <span>·</span>
                <span className="text-[#00FF41]">best: {lab.progress.best_score}/100</span>
              </>
            )}
          </div>

          <div className="bg-[#141416] border border-zinc-800 p-6 mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-3">scenario</div>
            <p className="text-zinc-200 leading-relaxed">{lab.scenario}</p>
          </div>

          <div className="bg-[#141416] border border-zinc-800 p-6 mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-3">
              objectives
            </div>
            <ul className="space-y-2">
              {lab.objectives.map((o, i) => (
                <li key={`brief-obj-${i}`} className="flex gap-3 text-sm text-zinc-200">
                  <span className="font-term text-[#00E5FF]">{String(i + 1).padStart(2, "0")}</span>
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="h-64 border border-zinc-800 bg-[#141416] mb-8">
            <TopologyDiagram topology={lab.topology} />
          </div>

          <button
            onClick={start}
            disabled={starting}
            className="px-8 py-4 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-2 glow-cyan disabled:opacity-50"
            data-testid="lab-start"
          >
            {starting ? <><Loader2 className="animate-spin" size={16} /> spinning up…</> : <>start lab →</>}
          </button>
        </main>
        <UpgradeModal
          open={upgrade.open}
          onClose={() => setUpgrade({ open: false, reason: null })}
          reason={upgrade.reason}
        />
      </div>
    );
  }

  // Active session — 3-panel desktop / tabbed mobile workspace
  const ObjectivesPanel = (
    <aside className="h-full bg-[#0A0A0A] overflow-y-auto p-4 relative">
      <div className="hidden md:flex absolute top-3 right-3 items-center gap-1">
        <button
          onClick={sounds.toggleMuted}
          title={sounds.muted ? "Unmute lab sounds" : "Mute lab sounds"}
          className="text-zinc-500 hover:text-white p-1"
          data-testid="lab-mute-btn"
          aria-pressed={sounds.muted}
        >
          {sounds.muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        <button
          onClick={toggleSidebar}
          title="Hide objectives"
          className="text-zinc-500 hover:text-white p-1"
          data-testid="lab-sidebar-close"
        >
          <X size={14} />
        </button>
      </div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-3 flex items-center gap-2">
        <CheckSquare size={12} /> objectives
      </div>
      <ul className="space-y-2" data-testid="lab-objectives">
        {lab.objectives.map((o, i) => (
          <li key={`active-obj-${i}`} className="flex gap-2 p-3 bg-[#141416] border border-zinc-800 text-xs">
            <span className="font-term text-[#00E5FF] shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-zinc-200 leading-relaxed">{o}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 border border-zinc-800 bg-[#141416]" data-testid="cli-quick-reference">
        <button
          type="button"
          onClick={toggleCliRef}
          aria-expanded={cliRefOpen}
          className="w-full flex items-center justify-between px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-zinc-200 font-mono transition-colors"
          data-testid="cli-quick-reference-toggle"
        >
          <span>cli quick reference</span>
          {cliRefOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <div
          className="grid transition-all duration-200 ease-out"
          style={{ gridTemplateRows: cliRefOpen ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <ul className="text-xs text-zinc-400 space-y-1.5 font-mono px-4 pb-4">
              <li><span className="text-[#00FF41]">?</span> · context help (try <span className="text-[#00FF41]">ip ?</span>, <span className="text-[#00FF41]">show ip ?</span>)</li>
              <li><span className="text-[#00FF41]">en</span> · enter privileged mode</li>
              <li><span className="text-[#00FF41]">conf t</span> · global config</li>
              <li><span className="text-[#00FF41]">int g0/0</span> · enter interface</li>
              <li><span className="text-[#00FF41]">do show run</span> · exec from config</li>
              <li><span className="text-[#00FF41]">end</span> · back to # · <span className="text-[#00FF41]">exit</span> · up one level</li>
              <li><span className="text-[#00FF41]">| include</span> / <span className="text-[#00FF41]">| section</span> · filter output</li>
              <li><span className="text-[#00FF41]">no &lt;cmd&gt;</span> · negate any config line</li>
              <li className="text-zinc-500">↑ / ↓ history · Tab complete · Ctrl-L clear · SPACE pages --More--</li>
              <li className="text-zinc-500 pt-2 border-t border-zinc-800 mt-2 hidden md:list-item">drag the grey handles to resize panels</li>
            </ul>
          </div>
        </div>
      </div>

              <div className="mt-4 flex flex-col gap-2" data-testid="lab-nav-cta">
                {(() => {
                  const bestScore = Math.max(
                    Number(lab.progress?.best_score) || 0,
                    Number(gradeResult?.score) || 0,
                  );
                  const passed = bestScore >= 100;
                  if (lab.next_lab_id) {
                    return (
                      <button
                        onClick={() => passed && nav(`/labs/${lab.next_lab_id}`)}
                        disabled={!passed}
                        title={passed
                          ? "Move on to the next lab"
                          : "Reach a score of 100 to unlock the next lab"}
                        className={`w-full px-3 py-2.5 font-semibold transition-colors flex items-center justify-center gap-2 ${
                          passed
                            ? "bg-[#00E5FF] text-black hover:bg-[#00B8CC] cursor-pointer"
                            : "bg-zinc-800 text-zinc-500 cursor-not-allowed border border-zinc-700"
                        }`}
                        data-testid="lab-next-lab"
                      >
                        {passed
                          ? <>next lab <ArrowRight size={14} /></>
                          : <>locked · score {bestScore}/100</>}
                      </button>
                    );
                  }
                  return (
                    <button
                      onClick={() => nav("/labs")}
                      className="w-full px-3 py-2.5 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center justify-center gap-2"
                      data-testid="lab-back-to-catalog"
                    >
                      back to catalog
                    </button>
                  );
                })()}
                <button
                  onClick={grade}
                  disabled={grading}
                  className="w-full px-3 py-2.5 border border-zinc-700 hover:border-[#00FF41] text-zinc-200 hover:text-[#00FF41] uppercase tracking-widest font-mono transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="lab-complete-lab"
                >
                  {grading ? <><Loader2 className="animate-spin" size={12} /> grading…</> : <>complete lab</>}
                </button>
              </div>
    </aside>
  );

  return (
    <div className="h-screen bg-[#0A0A0A] flex flex-col overflow-hidden">
      <Navbar />
      <div className="border-b border-zinc-800 px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between bg-[#0A0A0A] gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <Link to="/labs" className="text-xs text-zinc-500 hover:text-zinc-200 font-mono flex items-center gap-1 shrink-0" data-testid="lab-back-active">
            <ArrowLeft size={12} /> <span className="hidden sm:inline">exit</span>
          </Link>
          <div className="truncate min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono hidden sm:block">
              {lab.topic} · {lab.difficulty}
            </div>
            <div className="font-display font-bold text-sm sm:text-base truncate">{lab.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            onClick={retry}
            className="p-2 sm:px-3 sm:py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs font-mono uppercase tracking-widest flex items-center gap-1"
            data-testid="lab-reset"
            title="Reset lab"
          >
            <RotateCcw size={12} /> <span className="hidden sm:inline">reset</span>
          </button>
          {primaryIsPc && (
            <button
              onClick={() => setPcMenu({ open: true, deviceId: primaryDeviceMeta.id })}
              className="p-2 sm:px-3 sm:py-2 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 text-xs font-mono uppercase tracking-widest flex items-center gap-1"
              data-testid="pc-desktop-btn"
              title="Open PC desktop"
            >
              <span className="hidden sm:inline">desktop</span>
              <span className="sm:hidden">pc</span>
            </button>
          )}
          {(lab?.cml_eligible || lab?.cml_topology_id) && !realIosMode && (
            <button
              onClick={() => {
                if (billing?.tier !== "pro") {
                  setUpgrade({ open: true, reason: "real_ios" });
                  return;
                }
                setRealIosMode(true);
                closeSecondaryConsole();
              }}
              className="p-2 sm:px-3 sm:py-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 text-xs font-mono uppercase tracking-widest flex items-center gap-1"
              data-testid="open-real-ios-btn"
              title={billing?.tier === "pro" ? "Boot real Cisco IOS hardware via CML" : "Pro tier required for real IOS"}
            >
              <Cpu size={12} />
              <span className="hidden sm:inline">real ios</span>
              <span className="sm:hidden">ios</span>
            </button>
          )}
          {realIosMode && (
            <span className="px-3 py-2 text-xs font-mono uppercase tracking-widest border-2 border-emerald-500 bg-emerald-500 text-black dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/70 flex items-center gap-1 font-bold shadow-sm shadow-emerald-500/30"
              data-testid="real-ios-mode-pill">
              <Cpu size={12} /><span className="hidden sm:inline">real ios mode</span><span className="sm:hidden">ios</span>
            </span>
          )}
          <button
            onClick={() => setHintOpen(true)}
            className="p-2 sm:px-3 sm:py-2 border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 text-xs font-mono uppercase tracking-widest flex items-center gap-1"
            data-testid="lab-hint"
            title="Get AI hint"
          >
            <Lightbulb size={12} /> <span className="hidden sm:inline">hint</span>
          </button>
          <button
            onClick={grade}
            disabled={grading}
            className="px-2.5 sm:px-4 py-2 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm disabled:opacity-50"
            data-testid="lab-grade"
          >
            {grading ? (
              <><Loader2 className="animate-spin" size={14} /> grading…</>
            ) : (
              <><Award size={14} /> <span className="md:hidden">grade</span><span className="hidden md:inline">grade my work</span></>
            )}
          </button>
          <button
            onClick={toggleSidebar}
            title={sidebarOpen ? "Hide objectives panel" : "Show objectives panel"}
            className="hidden md:block p-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 transition-colors"
            data-testid="lab-toggle-sidebar"
          >
            <Menu size={14} />
          </button>
        </div>
      </div>

      {lab?.fault_lab && <FaultLabBanner realIos={realIosMode} />}

      {/* MOBILE TABS — only visible on < md */}
      <div className="md:hidden border-b border-zinc-800 flex bg-[#0A0A0A]" data-testid="mobile-tabs">
        {[
          { id: "terminal", label: "console" },
          { id: "topology", label: "topology" },
          { id: "objectives", label: "objectives" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setMobileTab(t.id)}
            className={`flex-1 py-2.5 text-[10px] uppercase tracking-[0.25em] font-mono transition-colors ${
              mobileTab === t.id
                ? "text-[#00E5FF] border-b-2 border-[#00E5FF]"
                : "text-zinc-500 border-b-2 border-transparent hover:text-zinc-300"
            }`}
            data-testid={`mobile-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* MOBILE BODY */}
      <div className="md:hidden flex-1 overflow-hidden min-h-0">
        {mobileTab === "terminal" && (
          realIosMode ? (
            <BootRealIOS
              labId={lab.id}
              deviceId={session.is_multi_device ? (activeDevices[0] || "R1") : (lab?.device_type === "switch" ? "SW1" : "R1")}
              simSessionId={session?.session_id}
              devices={session?.is_multi_device ? (session.devices || []).filter((d) => d.device_type !== "pc") : [{ id: (lab?.device_type === "switch" ? "SW1" : "R1"), device_type: lab?.device_type || "router" }]}
              isPro={billing?.tier === "pro"}
              onExit={() => { setRealIosMode(false); setV2SessionId(null); }}
              onV2SessionChange={setV2SessionId}
            />
          ) : (
          (() => {
            const dev = session.is_multi_device
              ? (session.devices || []).find((d) => d.id === activeDevices[0])
              : null;
            return (
              <Terminal
                key={dev?.id || "single"}
                sessionId={session.session_id}
                banner={dev ? `\nNow connected to ${dev.label} (${dev.device_type}). Type ? for help.\n` : session.banner}
                initialPrompt={dev?.prompt || session.prompt}
                deviceId={dev?.id}
                deviceLabel={dev?.label}
              />
            );
          })()
        ))}
        {mobileTab === "topology" && (
          <div className="h-full bg-[#141416]">
            <TopologyDiagram
              topology={lab.topology}
              activeDeviceIds={session.is_multi_device ? activeDevices : []}
              onDeviceClick={session.is_multi_device ? handleDeviceClick : null}
              dualMode={false}
            />
            {session.is_multi_device && (
              <div className="px-4 pb-3 text-[11px] uppercase tracking-[0.25em] font-mono text-zinc-500" data-testid="topology-hint-mobile">
                tap a device to switch console
              </div>
            )}
          </div>
        )}
        {mobileTab === "objectives" && ObjectivesPanel}
      </div>

      {/* DESKTOP 3-PANEL — only visible on >= md */}
      <div className="hidden md:block flex-1 overflow-hidden min-h-0">
        <PanelGroup direction="horizontal" style={{ height: "100%" }}>
          <Panel defaultSize={78} minSize={40}>
            <PanelGroup direction="vertical">
              <Panel
                ref={topoPanelRef}
                defaultSize={28}
                minSize={6}
                collapsible
                collapsedSize={0}
                onCollapse={() => setTopoCollapsed(true)}
                onExpand={() => setTopoCollapsed(false)}
              >
                <div className="h-full relative border-b border-zinc-800">
                  <TopologyDiagram
                    topology={lab.topology}
                    activeDeviceIds={session.is_multi_device ? activeDevices : []}
                    onDeviceClick={session.is_multi_device ? handleDeviceClick : null}
                    dualMode={session.is_multi_device}
                  />
                  {/* Collapse-to-console button — bottom-right, away from
                      the topology zoom controls at top-right. */}
                  <button
                    onClick={toggleTopo}
                    className="absolute bottom-2 right-3 z-20 px-2.5 py-1 bg-[#141416] border border-zinc-700 hover:border-[#00E5FF] text-zinc-300 hover:text-white text-[10px] uppercase tracking-[0.2em] font-mono transition-colors flex items-center gap-1"
                    data-testid="topology-collapse-btn"
                    title="Hide topology · maximize console"
                  >
                    <Minimize2 size={11} />
                    hide topology
                  </button>
                  {session.is_multi_device && (
                    <div className="absolute bottom-2 left-3 text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500" data-testid="topology-hint">
                      {realIosMode ? "click a device to switch its real console" : "click a device to switch · shift+click to open in 2nd console"}
                    </div>
                  )}
                  {session.is_multi_device && !realIosMode && (
                    <button
                      onClick={toggleSplit}
                      className="absolute bottom-2 right-40 px-2.5 py-1 bg-[#141416] border border-zinc-700 hover:border-[#00E5FF] text-zinc-300 hover:text-white text-[10px] uppercase tracking-[0.2em] font-mono transition-colors"
                      data-testid="toggle-split"
                    >
                      {splitMode ? "single console" : "split console"}
                    </button>
                  )}
                </div>
              </Panel>
              <PanelResizeHandle className="h-1.5 bg-zinc-900 hover:bg-[#00E5FF]/40 transition-colors flex items-center justify-center group" data-testid="resize-v">
                <GripHorizontal size={14} className="text-zinc-600 group-hover:text-[#00E5FF] transition-colors" />
              </PanelResizeHandle>
              <Panel defaultSize={72} minSize={20}>
                <div className="relative h-full">
                {/* When the topology is collapsed, surface a small floating
                    chip in the console area to bring it back. */}
                {topoCollapsed && (
                  <button
                    onClick={toggleTopo}
                    className="absolute top-2 right-3 z-20 px-2.5 py-1 bg-[#141416] border border-zinc-700 hover:border-[#00E5FF] text-zinc-300 hover:text-white text-[10px] uppercase tracking-[0.2em] font-mono transition-colors flex items-center gap-1 shadow-lg"
                    data-testid="topology-show-btn"
                    title="Show topology"
                  >
                    <Maximize2 size={11} />
                    show topology
                  </button>
                )}
                {/* Terminal area — single or split */}
                {session.is_multi_device && splitMode && activeDevices.length === 2 ? (
                  <PanelGroup direction="horizontal" style={{ height: "100%" }}>
                    <Panel defaultSize={50} minSize={20}>
                      {(() => {
                        const dev = (session.devices || []).find((d) => d.id === activeDevices[0]);
                        return (
                          <Terminal
                            key={`p-${dev.id}`}
                            sessionId={session.session_id}
                            banner={`\nNow connected to ${dev.label} (${dev.device_type}). Type ? for help.\n`}
                            initialPrompt={dev.prompt}
                            deviceId={dev.id}
                            deviceLabel={dev.label}
                          />
                        );
                      })()}
                    </Panel>
                    <PanelResizeHandle className="w-1.5 bg-zinc-900 hover:bg-[#F59E0B]/40 transition-colors flex items-center justify-center group" data-testid="resize-h-split">
                      <GripVertical size={14} className="text-zinc-600 group-hover:text-[#F59E0B] transition-colors" />
                    </PanelResizeHandle>
                    <Panel defaultSize={50} minSize={20}>
                      <div className="h-full relative">
                        {(() => {
                          const dev = (session.devices || []).find((d) => d.id === activeDevices[1]);
                          return (
                            <Terminal
                              key={`s-${dev.id}`}
                              sessionId={session.session_id}
                              banner={`\nNow connected to ${dev.label} (${dev.device_type}). Type ? for help.\n`}
                              initialPrompt={dev.prompt}
                              deviceId={dev.id}
                              deviceLabel={dev.label}
                            />
                          );
                        })()}
                        <button
                          onClick={closeSecondaryConsole}
                          className="absolute top-1.5 right-2 text-zinc-500 hover:text-white p-1 z-10"
                          title="Close this console"
                          data-testid="close-secondary-console"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </Panel>
                  </PanelGroup>
                ) : (
                  realIosMode ? (
                    <BootRealIOS
                      labId={lab.id}
                      deviceId={session.is_multi_device ? (activeDevices[0] || "R1") : (lab?.device_type === "switch" ? "SW1" : "R1")}
                      simSessionId={session?.session_id}
              devices={session?.is_multi_device ? (session.devices || []).filter((d) => d.device_type !== "pc") : [{ id: (lab?.device_type === "switch" ? "SW1" : "R1"), device_type: lab?.device_type || "router" }]}
                      isPro={billing?.tier === "pro"}
                      onExit={() => { setRealIosMode(false); setV2SessionId(null); }}
                      onV2SessionChange={setV2SessionId}
                            />
                  ) : (
                  (() => {
                    const dev = session.is_multi_device
                      ? (session.devices || []).find((d) => d.id === activeDevices[0])
                      : null;
                    return (
                      <Terminal
                        key={dev?.id || "single"}
                        sessionId={session.session_id}
                        banner={dev ? `\nNow connected to ${dev.label} (${dev.device_type}). Type ? for help.\n` : session.banner}
                        initialPrompt={dev?.prompt || session.prompt}
                        deviceId={dev?.id}
                        deviceLabel={dev?.label}
                      />
                    );
                  })()
                ))}
                </div>
              </Panel>
            </PanelGroup>
          </Panel>
          <PanelResizeHandle className="w-1.5 bg-zinc-900 hover:bg-[#00E5FF]/40 transition-colors flex items-center justify-center group" data-testid="resize-h">
            <GripVertical size={14} className="text-zinc-600 group-hover:text-[#00E5FF] transition-colors" />
          </PanelResizeHandle>
          <Panel
            ref={sidebarPanelRef}
            defaultSize={22}
            minSize={12}
            collapsible
            collapsedSize={0}
            onCollapse={() => setSidebarOpen(false)}
            onExpand={() => setSidebarOpen(true)}
          >
            <div className="h-full border-l border-zinc-800">
              {ObjectivesPanel}
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* Hint Panel */}
      {hintOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 backdrop-blur-md bg-black/70" data-testid="hint-modal">
          <div className="w-full max-w-lg bg-[#141416] border border-zinc-800">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#F59E0B] font-mono">
                <Lightbulb size={12} /> ai hint
              </div>
              <button onClick={() => setHintOpen(false)} className="text-zinc-500 hover:text-zinc-100" data-testid="hint-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <textarea
                value={hintQ}
                onChange={(e) => setHintQ(e.target.value)}
                placeholder="what's tripping you up? (optional)"
                className="w-full bg-black border border-zinc-800 focus:border-[#F59E0B] p-3 text-sm font-mono outline-none min-h-[80px]"
                data-testid="hint-input"
              />
              <button
                onClick={askHint}
                disabled={hintLoading}
                className="px-4 py-2 bg-[#F59E0B] text-black font-semibold text-sm hover:bg-[#D97706] transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="hint-submit"
              >
                {hintLoading ? <><Loader2 className="animate-spin" size={14} /> thinking…</> : "get hint"}
              </button>
              {hintResp && (
                <div className="bg-black border border-[#F59E0B]/40 p-4 text-sm text-zinc-200 leading-relaxed" data-testid="hint-response">
                  {hintResp}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <GradingModal
        open={gradeOpen}
        result={gradeResult}
        lab={lab}
        onClose={() => setGradeOpen(false)}
        onRetry={retry}
        onNextLab={() => {
          setGradeOpen(false);
          if (lab?.next_lab_id) {
            nav(`/labs/${lab.next_lab_id}`);
          } else {
            nav("/labs");
          }
        }}
      />
      <PcConfigModal
        open={pcConfigOpen}
        sessionId={session?.session_id}
        deviceId={pcConfigDevice?.id}
        deviceLabel={pcConfigDevice?.label}
        initial={pcConfigInitial}
        onClose={() => setPcConfigOpen(false)}
      />
      <PcBrowserModal
        open={pcBrowserOpen}
        sessionId={session?.session_id}
        deviceId={pcBrowserDevice?.id}
        deviceLabel={pcBrowserDevice?.label}
        onClose={() => setPcBrowserOpen(false)}
      />
      <PcMenu
        open={pcMenu.open}
        deviceLabel={(session?.devices || []).find((d) => d.id === pcMenu.deviceId)?.label}
        onClose={() => setPcMenu((m) => ({ ...m, open: false }))}
        onPickConsole={() => openPcConsole(pcMenu.deviceId)}
        onPickIpConfig={() => openPcConfig(pcMenu.deviceId)}
        onPickBrowser={() => openPcBrowser(pcMenu.deviceId)}
      />
      <UpgradeModal
        open={upgrade.open}
        onClose={() => setUpgrade({ open: false, reason: null })}
        reason={upgrade.reason}
      />
    </div>
  );
}
